import * as PopoverPrimitive from '@radix-ui/react-popover';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';

import { useDesignSystemIntl } from '../hooks/use-design-system-intl';

import { Button } from '../button';
import {
  spotlightBodyStyles,
  spotlightCloseButtonStyles,
  spotlightContentStyles,
  spotlightDescriptionStyles,
  spotlightFooterStyles,
  spotlightHeaderIconStyles,
  spotlightHeaderStyles,
  spotlightImageStyles,
  spotlightProgressStyles,
  spotlightTitleStyles,
} from './spotlight.styles';
import type {
  SpotlightArrowProps,
  SpotlightBodyProps,
  SpotlightCloseProps,
  SpotlightContentProps,
  SpotlightDescriptionProps,
  SpotlightFooterProps,
  SpotlightHeaderProps,
  SpotlightImageProps,
  SpotlightProgressProps,
  SpotlightTitleProps,
} from './spotlight.types';

/**
 * Root component for Spotlight.
 * Manages open/close state.
 */
const Spotlight = PopoverPrimitive.Root;
Spotlight.displayName = 'Spotlight';

/**
 * Trigger component that opens the Spotlight.
 */
const SpotlightTrigger = PopoverPrimitive.Trigger;
SpotlightTrigger.displayName = PopoverPrimitive.Trigger.displayName;

/**
 * Optional anchor component for custom positioning.
 */
const SpotlightAnchor = PopoverPrimitive.Anchor;
SpotlightAnchor.displayName = PopoverPrimitive.Anchor.displayName;

/**
 * Opinionated close button with X icon.
 * Renders as a ghost button styled for dark backgrounds.
 */
const SpotlightClose = React.forwardRef<HTMLButtonElement, SpotlightCloseProps>(
  (
    { 'data-testid': testId, 'aria-label': ariaLabel, className, children, ...props },
    ref
  ) => {
    const intl = useDesignSystemIntl();
    const closeLabel = intl.formatMessage({
      defaultMessage: 'Close',
      id: '2MqNgEx1Vu',
      description: 'Default accessible label for the Spotlight close button',
    });
    return (
      <PopoverPrimitive.Close asChild {...props}>
        <Button
          ref={ref}
          data-testid={testId}
          variant="ghost"
          size="xxs"
          iconOnly
          flush="all"
          className={cn(spotlightCloseButtonStyles, className)}
          aria-label={ariaLabel ?? closeLabel}
        >
          {children ?? <X />}
        </Button>
      </PopoverPrimitive.Close>
    );
  }
);
SpotlightClose.displayName = 'SpotlightClose';

/**
 * The floating content container with card-like styling.
 */
const SpotlightContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  SpotlightContentProps
>(
  (
    { 'data-testid': testId, className, align = 'center', sideOffset = 8, ...props },
    ref
  ) => (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        data-testid={testId}
        align={align}
        sideOffset={sideOffset}
        className={cn(spotlightContentStyles, className)}
        {...props}
      />
    </PopoverPrimitive.Portal>
  )
);
SpotlightContent.displayName = 'SpotlightContent';

/**
 * Arrow component that points to the trigger.
 * Re-uses PopoverArrow from the Popover component.
 */
const SpotlightArrow = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Arrow>,
  SpotlightArrowProps
>(({ 'data-testid': testId, className, ...props }, ref) => (
  <PopoverPrimitive.Arrow
    ref={ref}
    data-testid={testId}
    className={cn('fill-callout-background', className)}
    {...props}
  />
));
SpotlightArrow.displayName = 'SpotlightArrow';

/**
 * Header container for icon, title, and close button.
 */
const SpotlightHeader = React.forwardRef<HTMLDivElement, SpotlightHeaderProps>(
  ({ 'data-testid': testId, className, icon, children, ...props }, ref) => (
    <div
      ref={ref}
      data-testid={testId}
      className={cn(spotlightHeaderStyles, className)}
      {...props}
    >
      {icon && <span className={spotlightHeaderIconStyles}>{icon}</span>}
      {children}
    </div>
  )
);
SpotlightHeader.displayName = 'SpotlightHeader';

/**
 * Title component for the tooltip heading.
 */
const SpotlightTitle = React.forwardRef<HTMLHeadingElement, SpotlightTitleProps>(
  ({ 'data-testid': testId, className, children, ...props }, ref) => (
    <h4
      ref={ref}
      data-testid={testId}
      className={cn(spotlightTitleStyles, className)}
      {...props}
    >
      {children}
    </h4>
  )
);
SpotlightTitle.displayName = 'SpotlightTitle';

/**
 * Description component for supporting text.
 */
const SpotlightDescription = React.forwardRef<
  HTMLParagraphElement,
  SpotlightDescriptionProps
>(({ 'data-testid': testId, className, ...props }, ref) => (
  <p
    ref={ref}
    data-testid={testId}
    className={cn(spotlightDescriptionStyles, className)}
    {...props}
  />
));
SpotlightDescription.displayName = 'SpotlightDescription';

/**
 * Body component for main content.
 */
const SpotlightBody = React.forwardRef<HTMLDivElement, SpotlightBodyProps>(
  ({ 'data-testid': testId, className, ...props }, ref) => (
    <div
      ref={ref}
      data-testid={testId}
      className={cn(spotlightBodyStyles, className)}
      {...props}
    />
  )
);
SpotlightBody.displayName = 'SpotlightBody';

/**
 * Image container component with proper aspect ratio styling.
 * Pass an img element or Next.js Image as children.
 */
const SpotlightImage = React.forwardRef<HTMLDivElement, SpotlightImageProps>(
  ({ 'data-testid': testId, className, ...props }, ref) => (
    <div
      ref={ref}
      data-testid={testId}
      className={cn(spotlightImageStyles, className)}
      {...props}
    />
  )
);
SpotlightImage.displayName = 'SpotlightImage';

/**
 * Footer component for action buttons.
 */
const SpotlightFooter = React.forwardRef<HTMLDivElement, SpotlightFooterProps>(
  ({ 'data-testid': testId, className, ...props }, ref) => (
    <div
      ref={ref}
      data-testid={testId}
      className={cn(spotlightFooterStyles, className)}
      {...props}
    />
  )
);
SpotlightFooter.displayName = 'SpotlightFooter';

/**
 * Progress counter component for the footer (e.g., "1/3").
 */
const SpotlightProgress = React.forwardRef<HTMLSpanElement, SpotlightProgressProps>(
  ({ 'data-testid': testId, className, ...props }, ref) => (
    <span
      ref={ref}
      data-testid={testId}
      className={cn(spotlightProgressStyles, className)}
      {...props}
    />
  )
);
SpotlightProgress.displayName = 'SpotlightProgress';

export {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightBody,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightImage,
  SpotlightProgress,
  SpotlightTitle,
  SpotlightTrigger,
};
